/**
 * LoadingSpinner Component
 *
 * UI/Aesthetics: Circular loading spinner with optional text, supports inline, centered, and basic layouts
 * Functions:
 * - Displays animated spinner with configurable size and color
 * - Optional text display below/beside spinner
 * - Three layout modes: inline (horizontal), centered (full container), basic (simple)
 * - Configurable minimum height for centered mode
 * Previous: Used MUI CircularProgress with Box layout and Typography for text
 * Now: Uses custom Spinner component (Lucide Loader2) with Tailwind classes for layout
 */

import React from 'react';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';

interface LoadingSpinnerProps {
  /** Size of the spinner */
  size?: 'sm' | 'default' | 'lg' | 'xl' | number;
  /** Text to display below the spinner */
  text?: string;
  /** Whether to center the spinner in its container */
  centered?: boolean;
  /** Custom CSS classes */
  className?: string;
  /** Color variant */
  color?:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'destructive'
    | 'info'
    | 'success'
    | 'warning';
  /** Whether to show as inline (no padding/centering) */
  inline?: boolean;
  /** Minimum height for centered spinners (in rem, px, or number for px) */
  minHeight?: string | number;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'lg',
  text,
  centered = true,
  className,
  color = 'primary',
  inline = false,
  minHeight = '12.5rem', // 200px equivalent
}) => {
  // Convert numeric size to appropriate string variant
  const getSpinnerSize = (): 'sm' | 'default' | 'lg' | 'xl' => {
    if (typeof size === 'number') {
      if (size <= 16) return 'sm';
      if (size <= 24) return 'default';
      if (size <= 32) return 'lg';
      return 'xl';
    }
    return size;
  };

  // Convert numeric minHeight to string
  const getMinHeight = (): string => {
    if (typeof minHeight === 'number') {
      return `${minHeight}px`;
    }
    return minHeight;
  };

  const spinnerSize = getSpinnerSize();
  const minHeightValue = getMinHeight();

  if (inline) {
    return (
      <div className={cn('inline-flex items-center gap-2', className)}>
        <Spinner size={spinnerSize} color={color} />
        {text && <span className="text-sm text-muted-foreground">{text}</span>}
      </div>
    );
  }

  if (centered) {
    return (
      <div
        className={cn(
          'flex flex-col items-center justify-center gap-4',
          className,
        )}
        style={{ minHeight: minHeightValue }}
      >
        <Spinner size={spinnerSize} color={color} />
        {text && (
          <p className="text-sm text-muted-foreground text-center">{text}</p>
        )}
      </div>
    );
  }

  return (
    <div className={cn('', className)}>
      <Spinner size={spinnerSize} color={color} />
      {text && <p className="text-sm text-muted-foreground mt-2">{text}</p>}
    </div>
  );
};

export default LoadingSpinner;
